<template>
  <div class="menu-container-shell">
    <MobilePlatformModuleGrid
      v-if="shouldShowMobileModuleGrid"
      :title="partnerHomeTitle"
      :logo="logo"
      :items="mobilePlatformModules"
      :is-loading="isPlatformNavigationLoading"
      :get-item-title="getPlatformItemTitle"
      @item-click="onMobilePlatformModuleClick"
    />

    <MainMenu
      v-if="shouldShowLegacyMobileMenu"
      :userHasRelevantFF="true"
      :has-floating-bottom-bar="hasMobileNavigationRedesign"
      :show-menu-title-header="hasMobileNavigationRedesign"
      :menu-title="mobileMenuTitle"
      :show-home-button="shouldShowMobilePlatformHomeButton"
      :home-button-label="partnerHomeTitle"
      :home-button-logo="logo"
      :staffData="staffData"
      :profileAvatar="profileAvatar"
      :mappedMenuItems="mobileMenuItems"
      :isMenuLoading="isLoading"
      :checklistProgress="checklistProgress"
      :conversationCounts="counts"
      @menuItemClick="onMenuItemClick"
      @subMenuItemClick="onMenuItemClick"
      @rightAreaClick="onRightAreaClick"
      @homeClick="showMobileModuleSelection"/>

    <!-- Legacy MainMenuWide - when platform navigation FF is disabled -->
    <MainMenuWide v-if="!isMobileView && !hasPlatformNavigation"
                  :is-menu-loading="isLoading"
                  :mappedMenuItems="mappedMenuItems[0]"
                  :checklistProgress="checklistProgress"
                  :conversationCounts="counts"
                  :is-actions-disabled="isActionsDisabled"
                  @menuItemClick="onMenuItemClick"
                  @rightAreaClick="onRightAreaClick"/>

    <!-- Old menu sidebar - shown when platform navigation is active and active item is BMP with no platform sidebar -->
    <MainMenuWide v-if="!isMobileView && hasPlatformNavigation && isActiveItemBMP && !shouldShowPlatformSidebar"
                  :is-menu-loading="isLoading"
                  :mappedMenuItems="bmpMenuItems"
                  :checklistProgress="checklistProgress"
                  :conversationCounts="counts"
                  :is-actions-disabled="isActionsDisabled"
                  :show-logo="false"
                  @menuItemClick="onMenuItemClick"
                  @rightAreaClick="onRightAreaClick"/>

    <!-- Platform Navigation sidebar - when FF is enabled and active item has children -->
    <MainMenuWide v-if="!isMobileView && hasPlatformNavigation && shouldShowPlatformSidebar"
                  remove-quick-action
                  :is-menu-loading="isPlatformNavigationLoading"
                  :mappedMenuItems="platformSidebarItems"
                  :checklistProgress="checklistProgress"
                  :conversationCounts="counts"
                  :show-logo="false"
                  @menuItemClick="onPlatformMenuItemClick"
                  @rightAreaClick="onRightAreaClick"/>

    <BottomNavContainer v-if="showLegacyBottomNav" class="bottom-bar pointer-events-all"/>
  </div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import loggerService from '@/modules/common/services/loggerService';
import LayoutConstants from '@/constansts/layoutConstants';
import MainMenu from '@/modules/layout/mainMenu/components/MainMenu.vue';
import MainMenuWide from '@/modules/layout/mainMenu/components/MainMenuWide.vue';
import BottomNavContainer from '@/modules/layout/bottomNav/components/BottomNavContainer.vue';
import MenuItemsUtils from '@/modules/layout/mainMenu/utils/menuItemsUtils';
import handleNavigation from '@/utils/navigationUtils';
import { hasAction, executeAction } from '@/modules/layout/platformNavigation/utils/actionExecutor';
import { hasChildren } from '@/modules/layout/platformNavigation/utils/navigationUtils';
import {
  buildPathSet,
  filterMenuGroupsByPathSet,
  filterMenuItemsByPathSet,
} from '@/modules/layout/platformNavigation/utils/mobileNavigationDuplicateFiltering';
import { trackNavItemClick } from '@/modules/layout/platformNavigation/utils/navigationAnalytics';
import { getOnlinePresenceTranslationKey } from '@/utils/translationHelpers';
import MobilePlatformModuleGrid
  from '@/modules/layout/platformNavigation/components/mobile/MobilePlatformModuleGrid.vue';

const MOBILE_PLATFORM_NAV_FLAG = 'rollout.platform_administration.mobile_platform_nav';

export default {
  name: 'MenuContainer',
  components: {
    MobilePlatformModuleGrid,
    BottomNavContainer,
    MainMenuWide,
    MainMenu,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
    isActionsDisabled: {
      type: Boolean,
      default: false,
    },
  },
  async created() {
    try {
      await Promise.all([
        // FIXME: All of the following modules loading is delaying the main app loading by occupying all the http requests capacity
        this.addEssentialI18nModule('layout-pov'),
        this.addEssentialI18nModule('payments-pov'),
        this.addEssentialI18nModule('my-livesite-pov'),
        this.addEssentialI18nModule('quick-actions-pov'),
        this.addEssentialI18nModule('onboarding-pov'),
        this.addEssentialI18nModule('calendar-sync-pov'),
      ]);
      this.updateIsMenuTranslationLoading(false);
    } catch (e) {
      this.updateIsMenuTranslationLoading(true);
    }
  },
  watch: {
    shouldShowPlatformSidebar: {
      handler(showPlatformSidebar) {
        if (!showPlatformSidebar && this.hasPlatformNavigation && this.isActiveItemBMP && !this.allMenuItemsLoaded) {
          this.aggregateMenuItemsForStaff();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('MainMenuStore', ['updateIsMenuTranslationLoading',
      'updateShouldShowMenu',
      'fetchApplicationsMenuItems',
      'dispatchMenuItemAction',
      'dispatchPovMenuItemAction',
      'aggregateMenuItemsForStaff']),
    ...mapActions('ModalStore', ['setForceModalMode']),
    ...mapActions('PlatformNavigationStore', {
      platformHandleItemClick: 'handleItemClick',
      platformHandleSidebarItemClick: 'handleSidebarItemClick',
      showMobileModuleGrid: 'showMobileModuleGrid',
      showMobileModuleMenu: 'showMobileModuleMenu',
    }),
    ...mapActions('LayoutBottomNavStore', {
      setBottomNavCurrentIndex: 'setCurrentIndex',
    }),
    onMenuItemClick(item) {
      if (this.isActionsDisabled && item?.name !== 'logout') return;
      if (this.shouldUseMobilePlatformMenuItems) {
        this.onPlatformMenuItemClick(item);
        return;
      }
      trackNavItemClick(item.name, item, 'sidebar');
      this.fireMenuItemAction(item);
    },
    onPlatformMenuItemClick(item) {
      if (this.isActionsDisabled) return;
      trackNavItemClick(item.key || item.name, item, 'sidebar');
      const navItem = {
        ...item,
        url: item.url_path?.replace(/^\/app/, '') || item.url,
        action: item.action || null,
      };
      this.platformHandleSidebarItemClick({ item: navItem });
      if (hasAction(navItem)) {
        executeAction(navItem.action);
      }
      if (this.isMobileView) {
        this.updateShouldShowMenu(false);
      }
    },
    onMobilePlatformModuleClick(item) {
      trackNavItemClick(item.key || item.name, item, 'mobile_module_grid');
      this.showMobileModuleMenu();
      this.setBottomNavCurrentIndex(0);
      this.platformHandleItemClick({ item });
      if (hasAction(item) && !hasChildren(item) && !item.isBMP) {
        executeAction(item.action);
      }
      this.updateShouldShowMenu(false);
    },
    showMobileModuleSelection() {
      this.showMobileModuleGrid();
    },
    fireMenuItemAction(item) {
      if (item?.dispatchAction) {
        this.$store.dispatch(item.dispatchAction);
      }
      if (item?.isDeepLink) {
        this.updateShouldShowMenu(false);
        const itemPath = item?.url_path.startsWith('/') ? item?.url_path : `/${item?.url_path}`;
        handleNavigation({ path: itemPath, queryParams: item?.url_path_params || {} });
      } else if (item?.target) {
        this.updateShouldShowMenu(false);

        let povPath;
        let itemPath = item.target;
        if (item.url_path) {
          itemPath = item.url_path.startsWith('/app/') ? item.url_path.replace('/app', '') : item.url_path;
          povPath = this.$router.resolve(itemPath);
        }
        const isPovPage = povPath ? !povPath.route.meta.isAngular : item.type === 'pov';
        this.setForceModalMode(false);
        if (isPovPage) {
          this.$router.push(itemPath);
        } else {
          MenuItemsUtils.fireNavigationAction(item);
        }
      }
      if (item?.action) {
        this.dispatchMenuItemAction(item);
      }
      if (item?.povAction) {
        this.dispatchPovMenuItemAction(item.povAction);
      }
    },
    onRightAreaClick(item) {
      if (this.isActionsDisabled) return;
      if (item?.externalLink || item?.rightIconAction) {
        const itemWithAction = { ...item, action: item.externalLink || item.rightIconAction };
        this.dispatchMenuItemAction(itemWithAction);
      }
      this.rightClickAnalyticsTrack(item.name);
    },
    getTranslatedItem(item) {
      return {
        ...item,
        label: this.getItemLabel(item),
        badgeText: this.$t(item.badgeText),
      };
    },
    translateSubItems(subItems) {
      try {
        return subItems.map((item) => this.getTranslatedItem(item));
      } catch (error) {
        loggerService.error('MAIN-MENU-TRANSLATE-SUB-ITEMS', { subItems, error });
        return [];
      }
    },
    decorateSubItemAsMainCategory(item) {
      return {
        ...item.submenuItems[0],
        ...item,
        submenuItems: [],
      };
    },
    decorateCategoryWithSubItems(item) {
      return {
        ...item,
        submenuItems: this.translateSubItems(item.submenuItems),
      };
    },
    processMenuItem(item) {
      const isCategoryClicked = item.submenuItems?.length === 1;
      const menuItem = isCategoryClicked
        ? this.decorateSubItemAsMainCategory(item)
        : this.decorateCategoryWithSubItems(item);

      return this.getTranslatedItem(menuItem);
    },
    mapMenuGroupItems(menuItemsGroup) {
      try {
        return menuItemsGroup.map((item) => this.processMenuItem(item));
      } catch (error) {
        loggerService.error('MAIN-MENU-MAP-GROUP-ITEMS', { menuItemsGroup, error });
        return [];
      }
    },
    rightClickAnalyticsTrack(itemName) {
      if (itemName === 'client_portal_editor') {
        this.$track('Client Portal Menu Popout');
      }
    },
    /**
     * Remove items from an old-menu group whose url_path is present in pathSet.
     * Handles two structures produced by processMenuItem:
     *   - Single items promoted from one submenu item: have url_path directly
     *   - Category items with multiple submenu items: filter submenu items individually
     */
    filterMenuItemsByPaths(menuItems, pathSet) {
      return filterMenuItemsByPathSet(menuItems, pathSet);
    },
    filterMobilePlatformItems(menuItems) {
      if (!menuItems) return [];
      return menuItems.reduce((acc, item) => {
        if (!item.available_in_mobile) return acc;

        acc.push({
          ...item,
          submenuItems: this.filterMobilePlatformItems(item.submenuItems),
        });
        return acc;
      }, []);
    },
    getItemLabel(item) {
      if (item.name === LayoutConstants.MENU_DIRECTORY_WORK_AS) {
        return this.$t(item.translationKey, { staff: this.staffData.display_name });
      }

      // Handle conditional "Online Presence" vs "Websites" translation
      if (item.name === 'my-livesite') {
        const conditionalKey = getOnlinePresenceTranslationKey('my-livesite');
        return this.$t(`common.navigation.${conditionalKey}`);
      }

      return item.isAlreadyTranslated ? item.label : this.$t(item.translationKey);
    },
  },
  computed: {
    ...mapGetters('CommonStore', ['isInitComplete', 'isMobile', 'isMobileApp']),
    ...mapGetters('BusinessStore', ['hasFeature', 'attributes', 'brandName', 'logo']),
    ...mapGetters('MainMenuStore', ['isMenuTranslationLoading', 'shouldShowMenu', 'menuItems', 'checklistProgress', 'allMenuItemsLoaded']),
    ...mapGetters('StaffStore', ['staffData']),
    ...mapGetters('ConversationStore', ['counts']),
    ...mapGetters('PlatformNavigationStore', {
      activeTopBarItem: 'activeTopBarItem',
      getPlatformItemTitle: 'getItemTitle',
      mobilePlatformModules: 'mobileItems',
      mobileMenuMode: 'mobileMenuMode',
      platformSidebarItems: 'sidebarItems',
      shouldShowPlatformSidebar: 'shouldShowSidebar',
      isPlatformNavigationLoading: 'isLoading',
      isActiveItemBMP: 'isActiveItemBMP',
      nonBmpNavigationPaths: 'nonBmpNavigationPaths',
    }),
    isMobileView() {
      return this.isMobile || this.isMobileApp;
    },
    hasPlatformNavigation() {
      return this.hasFeature('rollout.platform_administration.platform_navigation');
    },
    hasMobileNavigationRedesign() {
      return this.hasFeature('rollout.platform_administration.mobile_glassy_nav_layout');
    },
    hasMobilePlatformNavigation() {
      return this.hasMobileNavigationRedesign && this.hasFeature(MOBILE_PLATFORM_NAV_FLAG);
    },
    showLegacyBottomNav() {
      return this.isMobileView && !this.hasMobileNavigationRedesign;
    },
    shouldShowMobileModuleGrid() {
      return this.isMobileView
        && this.shouldShowMenu
        && this.hasMobilePlatformNavigation
        && this.mobileMenuMode === 'module-grid';
    },
    shouldShowLegacyMobileMenu() {
      if (!this.isMobileView) return false;
      return this.shouldShowMenu && !this.shouldShowMobileModuleGrid;
    },
    shouldShowMobilePlatformHomeButton() {
      return this.hasMobilePlatformNavigation;
    },
    shouldUseMobilePlatformMenuItems() {
      return this.hasMobilePlatformNavigation && !this.isActiveItemBMP;
    },
    partnerHomeTitle() {
      const partnerName = this.brandName || this.attributes?.brand_name || 'vcita';
      return `${partnerName} Home`;
    },
    mobileMenuTitle() {
      if (!this.hasMobilePlatformNavigation) {
        return this.$t('layout.menu.topbar_title');
      }
      return this.activeTopBarItem
        ? this.getPlatformItemTitle(this.activeTopBarItem)
        : this.$t('layout.menu.topbar_title');
    },
    mobileMenuItems() {
      if (!this.hasMobilePlatformNavigation) {
        return this.mappedMenuItems;
      }
      if (this.isActiveItemBMP) {
        if (this.isMobileView) {
          return filterMenuGroupsByPathSet(this.mappedMenuItems, this.mobileBmpDuplicatePathSet);
        }
        return this.bmpMenuItems ? [this.bmpMenuItems] : [];
      }
      return [this.mobilePlatformSidebarItems];
    },
    mobilePlatformSidebarItems() {
      return this.filterMobilePlatformItems(this.platformSidebarItems);
    },
    mappedMenuItems() {
      const decoratedMenuItems = this.menuItems.map((menuItemsGroup) => this.mapMenuGroupItems(menuItemsGroup));
      return decoratedMenuItems.filter((group) => group.length > 0);
    },
    /**
     * BMP sidebar items with top-bar duplicates removed.
     * When platform navigation is active, any old-menu item whose url_path matches
     * a URL already reachable via the top bar (including children) is hidden.
     */
    bmpMenuItems() {
      const group = this.mappedMenuItems[0];
      if (!this.hasPlatformNavigation || !this.nonBmpNavigationPaths.length || !group) {
        return group;
      }
      return this.filterMenuItemsByPaths(group, this.desktopDuplicatePathSet);
    },
    mobileBmpDuplicatePathSet() {
      return this.desktopDuplicatePathSet;
    },
    desktopDuplicatePathSet() {
      return buildPathSet(this.nonBmpNavigationPaths);
    },
    profileAvatar() {
      return {
        colorId: this.staffData.color_id || 27, // TODO: figure out where to get this value from
        name: this.staffData.display_name,
        imagePath: this.staffData.photo_path || this.attributes.photo_path || '',
      };
    },
  },
};
</script>

<style lang="scss" scoped>
$bottom_bar_height: 48px;

.bottom-bar {
  height: $bottom_bar_height;
  position: absolute;
  bottom: 5px;
  width: 100%;
  z-index: 1;
}

.platform-bottom-bar-container {
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 3;
}
</style>
